import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import { TESTIMONIALS } from "@/lib/data";

export default function SuccessStories() {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  const next = () => setI((v) => (v + 1) % TESTIMONIALS.length);
  const prev = () => setI((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section className="py-24 sm:py-32" data-testid="success-stories">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Success Stories"
          title="Real students. Real US offers. Real careers."
          sub="From STEM OPT to Fortune 500 H-1B — hear directly from international students who built their US careers with SKVY Harmony."
        />

        <div className="mt-16 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="glass rounded-3xl p-8 sm:p-12 grid md:grid-cols-[1fr_320px] gap-8 items-center"
            >
              <div>
                <Quote className="w-10 h-10 text-[#10B981] mb-6" />
                <p className="font-poppins text-white text-2xl sm:text-3xl leading-snug font-medium tracking-tight">
                  "{t.quote}"
                </p>
                <div className="mt-8">
                  <p className="font-poppins text-white font-semibold text-lg">{t.name}</p>
                  <p className="font-inter text-sm text-white/70 mt-1">{t.role}</p>
                  <p className="font-inter text-sm text-[#10B981] mt-0.5">{t.company}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {t.visa && (
                      <span className="font-inter text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30">
                        {t.visa}
                      </span>
                    )}
                    {t.university && (
                      <span className="font-inter text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-white/5 text-white/60 border border-white/10">
                        {t.university}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                <img src={t.img} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(10,16,35,0.9))" }} />
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  aria-label={`Story ${idx + 1}`}
                  data-testid={`testimonial-dot-${idx}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-[#10B981]" : "w-2 bg-white/20"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} data-testid="testimonial-prev" aria-label="Previous"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/70 hover:text-white">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={next} data-testid="testimonial-next" aria-label="Next"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/70 hover:text-white">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
